/// <reference types="@types/google.maps" />

import { importLibrary, setOptions } from '@googlemaps/js-api-loader';
import React from 'react';

interface FixedAddress {
	original: string;
	placeId?: string;
	formattedAddress?: string;
	latitude?: number;
	longitude?: number;
	addressComponents?: google.maps.GeocoderAddressComponent[];
	rawPrediction?: google.maps.places.AutocompletePrediction;
	error?: string;
}

interface Options {
	/** Google Maps API key with Places enabled */
	apiKey: string;
	/** 2-letter ccTLD bias like "us" or "ca" */
	region?: string;
	/** BCP47 language tag like "en" */
	language?: string;
	/** Max concurrent lookups */
	concurrency?: number;
	/** Per-request component restrictions (e.g., { country: ["us"] }) */
	componentRestrictions?: google.maps.places.ComponentRestrictions;
	/** Optional delay between each request in ms (per worker) */
	delayMs?: number;
}

export function useBatchFixAddresses(
	inputs: string[],
	{
		apiKey,
		region = 'us',
		language = 'en',
		concurrency = 1,
		delayMs = 150,
		componentRestrictions,
	}: Options
) {
	const [results, setResults] = React.useState<Record<string, FixedAddress>>(
		{}
	);
	const [loading, setLoading] = React.useState(false);
	const [progress, setProgress] = React.useState({ done: 0, total: 0 });
	const [error, setError] = React.useState<string | null>(null);

	React.useEffect(() => {
		if (!inputs || inputs.length === 0) {
			setResults({});
			setProgress({ done: 0, total: 0 });
			setError(null);
			return;
		}

		let cancelled = false;

		async function run() {
			setLoading(true);
			setError(null);
			setProgress({ done: 0, total: inputs.length });

			try {
				setOptions({
					key: apiKey,
					v: 'weekly',
					region,
					language,
				});

				const { AutocompleteService, AutocompleteSessionToken } =
					await importLibrary('places');

				const dummy = document.createElement('div');
				const placesService = new google.maps.places.PlacesService(dummy);

				const svc: google.maps.places.AutocompleteService =
					new AutocompleteService();

				const getPredictions = (
					input: string,
					token: google.maps.places.AutocompleteSessionToken
				) =>
					new Promise<google.maps.places.AutocompletePrediction[]>(
						(resolve, reject) => {
							void svc.getPlacePredictions(
								{
									input,
									sessionToken: token,
									types: ['address'], // bias for addresses
									componentRestrictions,
								},
								(preds, status) => {
									if (status === 'OK' && preds) {
										resolve(preds);
									} else if (status === 'ZERO_RESULTS') {
										resolve([]);
									} else {
										reject(
											new Error(
												`getPlacePredictions failed (${status}) for "${input}"`
											)
										);
									}
								}
							);
						}
					);

				const getDetails = (
					placeId: string,
					token?: google.maps.places.AutocompleteSessionToken
				) =>
					new Promise<google.maps.places.PlaceResult>((resolve, reject) => {
						placesService.getDetails(
							{
								placeId,
								// Requesting minimal fields per docs:
								// https://developers.google.com/maps/documentation/places/web-service/legacy/details#fields
								fields: [
									'formatted_address',
									'address_components',
									'place_id',
									'geometry',
								],
								// Some SDKs accept sessionToken here for billing cohesion.
								// If unsupported, it will be ignored safely.
								// See session-token guidance:
								// https://developers.google.com/maps/documentation/places/web-service/using-session-tokens
								sessionToken: token,
							},
							(place, status) => {
								if (status === 'OK' && place) {
									resolve(place);
								} else {
									reject(
										new Error(
											`getDetails failed (${status}) for placeId=${placeId}`
										)
									);
								}
							}
						);
					});

				// 3) Worker pool for gentle concurrency
				const queue = [...inputs];
				const out: Record<string, FixedAddress> = {};
				let done = 0;

				async function sleep(ms: number) {
					return new Promise<void>((resolve) => setTimeout(resolve, ms));
				}

				async function worker() {
					while (queue.length && !cancelled) {
						const original = queue.shift()!;
						const token = new AutocompleteSessionToken(); // per session https://developers.google.com/maps/documentation/places/web-service/using-session-tokens
						try {
							const preds = await getPredictions(original, token);

							// Pick a "best" prediction.
							const pick =
								preds.find((p) =>
									(p.types || []).some((t) =>
										['street_address', 'premise', 'subpremise'].includes(t)
									)
								) || preds[0];

							if (pick) {
								const details = await getDetails(pick.place_id, token);
								out[original] = {
									original,
									placeId: pick.place_id,
									rawPrediction: pick,
									formattedAddress: details.formatted_address,
									latitude: details.geometry?.location?.lat(),
									longitude: details.geometry?.location?.lng(),
									addressComponents: details.address_components || [],
								};
							} else {
								out[original] = { original, error: 'No predictions' };
							}
						} catch (err: unknown) {
							out[original] = {
								original,
								error: (err as Error)?.message || 'Unknown error',
							};
						} finally {
							done += 1;
							if (!cancelled) setProgress({ done, total: inputs.length });

							if (!cancelled && delayMs > 0) {
								await sleep(delayMs);
							}
						}
					}
				}

				const workers = Array.from(
					{ length: Math.min(concurrency, inputs.length) },
					worker
				);
				await Promise.all(workers);

				if (!cancelled) {
					setResults(out);
				}
			} catch (err: unknown) {
				if (!cancelled)
					setError((err as Error)?.message || 'Failed to resolve addresses.');
			} finally {
				if (!cancelled) setLoading(false);
			}
		}

		void run();
		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [inputs]);

	return { results, loading, progress, error };
}
